import { gql } from 'urql';

import {
  MANDATORY_MASTER_FIELDS,
  MANDATORY_VARIANT_FIELDS,
  PDP_MANDATORY_EXTRA_VARIANT_FIELDS,
  PDP_MANDATORY_VARIANT_FIELDS,
  SIZES_FRAGMENT,
} from 'gql/fragments/product';

export const VARIANTS_QUERY = gql`
  query Variants(
    $input: VariantsInput!
    $includeComparisonFields: Boolean = false
  ) {
    variants(input: $input) {
      __typename
      id
      masterId
      productPrice {
        price
        salePrice
        promotionPrice
        isSalePriceElapsed
        tax
        taxRate
      }
      ean
      percentageDiscountBadge
      name
      variantId
      header
      subHeader
      orderable
      colorValue
      colorName
      displayOutOfStock {
        displayValue
      }
      images {
        href
        alt
      }
      technology @include(if: $includeComparisonFields)
      runTerrain @include(if: $includeComparisonFields)
      weight @include(if: $includeComparisonFields)
      heelToToeDrop @include(if: $includeComparisonFields)
      fastener @include(if: $includeComparisonFields)
      pronation @include(if: $includeComparisonFields)
      refinementCushioningLevel @include(if: $includeComparisonFields)
      runnerType @include(if: $includeComparisonFields)
      mainMaterial @include(if: $includeComparisonFields)
      cupType @include(if: $includeComparisonFields)
      trouserRise @include(if: $includeComparisonFields)
      pocketType @include(if: $includeComparisonFields)
      apparelLength @include(if: $includeComparisonFields)
      characteristics @include(if: $includeComparisonFields)
      supportLevelBra @include(if: $includeComparisonFields)
      studs @include(if: $includeComparisonFields)
      playerLevel @include(if: $includeComparisonFields)
      fit @include(if: $includeComparisonFields)
    }
  }
`;

export const SIBLING_VARIANTS = gql`
  query SiblingVariants($input: SiblingVariantsInput!) {
    siblingVariants(input: $input) {
      ...mandatoryVariantFields
      badges {
        id
        label
      }
      percentageDiscountBadge
      salePrice
      productPrice {
        price
        salePrice
        promotionPrice
        isSalePriceElapsed
        tax
        taxRate
      }
      displayOutOfStock {
        soldout
        soldoutWithRecommender
        comingsoon
        backsoon
        presale
        displayValue
      }
      orderable
      promotions(page: ProductListingPage) {
        id
        calloutMessage
      }
      isAppExclusive
      appOnlyDateTimeFrom
      appOnlyDateTimeTo
    }
  }

  ${MANDATORY_VARIANT_FIELDS}
`;

export const VARIANT_QUERY = gql`
  query Variant($id: ID!) {
    variant(id: $id) {
      id
      masterId
      name
      colorValue
      styleNumber
      size {
        value
      }
    }
  }
`;

export const PRODUCT_QUERY = gql`
  query Product($id: ID!) {
    product(id: $id) {
      id
      name
      header
      subHeader
      orderable
      displayOutOfStock {
        displayValue
      }
      variations {
        id
        name
        colorValue
        colorName
        preview
        price
        salePrice
        productPrice {
          price
          salePrice
          promotionPrice
        }
        orderable
        sizeGroups {
          label
          description
          sizes {
            id
            label
            value
            productId
            orderable
            maxOrderableQuantity
          }
        }
        displayOutOfStock {
          soldout
          soldoutWithRecommender
          comingsoon
          backsoon
          presale
          displayValue
          validTo
        }
        images {
          href
          alt
        }
      }
    }
  }
`;

export const PDP_QUERY = gql`
  query PDP($id: ID!, $viewAt: String) {
    product(id: $id) {
      ...mandatoryMasterFields
      productNameTranslated
      description
      primaryCategoryId
      productDivision
      brand
      disableRatings
      disableReviews
      amountOfReviews
      averageRating
      sizeChartId
      promotions(page: ProductDetailsPage) {
        id
        calloutMessage
      }
      promotion {
        id
        calloutMessage
      }
      configureID {
        enabled
        productID
      }
      productStory {
        longDescription
        careInstructions
        productKeywords
      }
      productVideos
      verticalProductVideos
      promotionExclusion
      exploreMoreCTALabel
      showExploreCollectionCTA
      exploreMoreCTATargetCategoryID
      brand
      orderable
      badge
      ...sizes
      fitOptions {
        label
        masterProduct {
          id
          name
          variations {
            id
            colorValue
            orderable
          }
        }
      }
      variations {
        ...pdpMandatoryVariantFields
        isFinalSale
        returnRuleID
        promotionExclusion
        specialMessage(viewAt: $viewAt)
        ean
        validUntil
        isAppExclusive
        badges {
          id
          label
        }
        percentageDiscountBadge
        salePrice
        productPrice {
          price
          salePrice
          promotionPrice
          isSalePriceElapsed
          tax
          taxRate
        }
        styleNumber
        materialComposition
        displayOutOfStock {
          soldout
          soldoutWithRecommender
          comingsoon
          backsoon
          presale
          displayValue
          validTo
        }
        orderable
        modelMeasurementText
        manufacturerInfo {
          manufacturerAddress {
            label
            content
          }
          countryOfOrigin {
            label
            content
          }
        }
        myCustomizer {
          enabled
          iframeID
          startPoint
        }
        configureID {
          enabled
          productID
        }
        taxDisplayMsg
        promotions(page: ProductDetailsPage) {
          id
          calloutMessage
        }
        appOnlyDateTimeFrom
        appOnlyDateTimeTo
      }
    }
  }

  ${PDP_MANDATORY_VARIANT_FIELDS}
  ${MANDATORY_MASTER_FIELDS}
  ${SIZES_FRAGMENT}
`;

// Lazy PDP query to fetch non-critical product data azynchronously after the page has loaded, reducing the initial render.
export const LAZY_PDP_QUERY = gql`
  query LazyPDP($id: ID!) {
    product(id: $id) {
      id
      variations {
        ...pdpMandatoryExtraVariantFields
        description
        productStory {
          longDescription
          materialComposition
          careInstructions
          manufacturerInfo {
            manufacturerAddress {
              label
              content
            }
            countryOfOrigin {
              label
              content
            }
          }
          productKeywords
        }
      }
    }
  }

  ${PDP_MANDATORY_EXTRA_VARIANT_FIELDS}
`;

export const PRODUCT_SIZE_CHART_QUERY = gql`
  query ProductSizeChart($id: [ID!]!) {
    sizeCharts(ids: $id) {
      id
      name
      body
    }
  }
`;
